import { Fragment, type ReactNode, useState } from 'react';

import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';

import { EmptyTableState } from '@nesso/ui/blocks/table/empty-table-state';
import { cn } from '@nesso/ui/lib/utils';
import { Button } from '@nesso/ui/primitives/button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@nesso/ui/primitives/table';

import { formatNumericShortDate, formatShortDate } from '@/shared/lib/time-utils';

type SessionChargeRow = {
    id: string;
    date: string;
    serviceCode: string;
};

export type SessionChargeColumn<Row> = {
    key: string;
    header: ReactNode;
    headClassName?: string;
    cellClassName?: string;
    cell: (sessionCharge: Row) => ReactNode;
};

type Props<Row extends SessionChargeRow> = {
    label: string;
    action: ReactNode;
    headerClassName?: string;
    count: number;
    sessionCharges: Row[];
    columns: SessionChargeColumn<Row>[];
    serviceHeadClassName?: string;
    detailsHeadClassName?: string;
    emptyMessage: string;
    renderBillingDetails: (sessionCharge: Row) => ReactNode;
};

export function ClientBillingSheetSessionChargesTable<Row extends SessionChargeRow>({
    label,
    action,
    headerClassName,
    count,
    sessionCharges,
    columns,
    serviceHeadClassName,
    detailsHeadClassName,
    emptyMessage,
    renderBillingDetails,
}: Props<Row>) {
    const [expandedIds, setExpandedIds] = useState<string[]>([]);
    const columnCount = columns.length + 2;

    function toggle(id: string) {
        setExpandedIds((ids) => (ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id]));
    }

    return (
        <section className="flex flex-col gap-4">
            <div className={cn(headerClassName, 'flex items-center justify-between gap-2')}>
                <h3 className="text-lg font-bold">
                    {count} {label} {count === 1 ? 'session' : 'sessions'}
                </h3>
                {action}
            </div>
            <Table className="table-fixed">
                <TableHeader>
                    <TableRow>
                        <TableHead variant="plain" className={serviceHeadClassName}>
                            Service
                        </TableHead>
                        {columns.map((column) => (
                            <TableHead
                                key={column.key}
                                variant="plain"
                                className={column.headClassName}
                            >
                                {column.header}
                            </TableHead>
                        ))}
                        <TableHead variant="plain" className={detailsHeadClassName}>
                            <span className="sr-only">Details</span>
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {count === 0 ? (
                        <EmptyTableState
                            colSpan={columnCount}
                            message={emptyMessage}
                            tone="default"
                        />
                    ) : (
                        sessionCharges.map((sessionCharge) => {
                            const isExpanded = expandedIds.includes(sessionCharge.id);
                            const date = formatNumericShortDate(sessionCharge.date);

                            return (
                                <Fragment key={sessionCharge.id}>
                                    <TableRow className="h-18">
                                        <TableCell>
                                            <div className="flex flex-col">
                                                <span className="font-semibold">
                                                    {sessionCharge.serviceCode}
                                                </span>
                                                <span className="text-muted-foreground">
                                                    {date}
                                                </span>
                                            </div>
                                        </TableCell>
                                        {columns.map((column) => (
                                            <TableCell
                                                key={column.key}
                                                className={column.cellClassName}
                                            >
                                                {column.cell(sessionCharge)}
                                            </TableCell>
                                        ))}
                                        <TableCell className="py-2 pr-1 pl-0.5 text-right">
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon-sm"
                                                aria-expanded={isExpanded}
                                                aria-label={`${isExpanded ? 'Hide' : 'Show'} billing details for ${sessionCharge.serviceCode} on ${formatShortDate(sessionCharge.date)}`}
                                                onClick={() => toggle(sessionCharge.id)}
                                            >
                                                {isExpanded ? (
                                                    <ChevronUpIcon />
                                                ) : (
                                                    <ChevronDownIcon />
                                                )}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                    {isExpanded && (
                                        <TableRow variant="detail">
                                            <TableCell colSpan={columnCount} className="px-6 py-6">
                                                {renderBillingDetails(sessionCharge)}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </Fragment>
                            );
                        })
                    )}
                </TableBody>
            </Table>
        </section>
    );
}
